import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { useLeadModalPrefill } from "../shared/useLeadModalPrefill.js";
import { setAuthSessionCookies } from "@/lib/authCookies";
import {
  mapTrialSignupError,
  registerUniversalTrialAccount,
  TRIAL_DUPLICATE_EMAIL_MESSAGE,
  UNIVERSAL_SIGNUP_DASHBOARD_ROUTE,
  UNIVERSAL_SIGNUP_SUCCESS_MESSAGE,
  trialSignupFailureGuide,
} from "@/lib/landingSignup";
import { trackRegistrationIfAuthenticated } from "@/lib/leadEvents";
import { canSubmitUniversalSignup, legacyBrainToSignupChoice, useSignupNiches } from "@/lib/signupNiches";
import SignupNicheSelect from "@/components/SignupNicheSelect";
import { CTA_MICRO_DISCLOSURE } from "../shared/ctaContent.js";
import LandingPriceLine from "@/pages/LandingPages/shared/LandingPriceLine";

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const MODAL_TITLE = "Put Your Roofing Digital Employee to Work";
const MODAL_SUBTITLE = "Enter your email to access your dashboard. Our concierge team will configure your 26-second response rules in 12 minutes.";
const SUBMIT_LABEL = "Hire Digital Employee & Open Dashboard ↗";

const LeadCaptureModal = ({ isOpen, onClose, entryLine, catalogStatus }) => {
  const [email, setEmail] = useState("");
  const [family, setFamily] = useState(null);
  const [nicheId, setNicheId] = useState(null);
  const [status, setStatus] = useState("form");
  const [submitLocked, setSubmitLocked] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [recoveryLinks, setRecoveryLinks] = useState([]);
  const choiceTouched = useRef(false);
  const submitting = useRef(false);
  const nicheCatalog = useSignupNiches();
  const { emailInputRef, initialBotType } = useLeadModalPrefill(isOpen, setEmail);

  useEffect(() => {
    if (!isOpen || choiceTouched.current) return;
    const choice = legacyBrainToSignupChoice(initialBotType);
    if (choice.family !== "universal") return;
    setFamily("universal");
    setNicheId(choice.nicheId);
  }, [isOpen, initialBotType]);

  useEffect(() => {
    const handleEsc = (event) => {
      if (event.key === "Escape" && status !== "loading") {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener("keydown", handleEsc);
      document.body.style.overflow = "hidden";
    }

    return () => {
      document.removeEventListener("keydown", handleEsc);
      document.body.style.overflow = "";
    };
  }, [isOpen, onClose, status]);

  useEffect(() => {
    if (!isOpen) {
      choiceTouched.current = false;
      setFamily(null);
      setNicheId(null);
      setStatus("form");
      setSubmitLocked(false);
      setErrorMessage("");
      setRecoveryLinks([]);
    }
  }, [isOpen]);

  const handleClose = () => {
    if (status === "loading") return;
    onClose();
  };

  const handleNicheChange = (nextId) => {
    if (status === "loading" || submitLocked || submitting.current) return;
    choiceTouched.current = true;
    setFamily(nextId ? "universal" : null);
    setNicheId(nextId);
  };

  const universalReady = canSubmitUniversalSignup({
    catalogStatus: nicheCatalog.status,
    nicheId,
    niches: nicheCatalog.niches,
  });
  const choiceReady = family === "universal" && universalReady;

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (status === "loading" || submitLocked || submitting.current) return;

    const normalizedEmail = email.trim();
    if (!EMAIL_RE.test(normalizedEmail)) {
      setErrorMessage("Please enter a valid email address.");
      return;
    }
    if (!choiceReady) return;

    setErrorMessage("");
    setRecoveryLinks([]);
    setStatus("loading");
    submitting.current = true;

    try {
      const data = await registerUniversalTrialAccount({
        email: normalizedEmail,
        nicheId,
      });

      if (data.status === "ok") {
        const token = typeof data.token === "string" ? data.token.trim() : "";
        const accepted = family === "universal" && data.bot_type === "universal";
        if (!token || !accepted) {
          setErrorMessage("Unexpected response from the server. Please try again.");
          setStatus("form");
          return;
        }

        const emailLower = normalizedEmail.toLowerCase();
        setAuthSessionCookies({
          token,
          email: emailLower,
          accountType: data.account_type || "client",
          botType: family,
        });
        trackRegistrationIfAuthenticated().catch((err) =>
          console.warn("[leadEvents] is_registered failed", err),
        );
        setStatus("success");
        return;
      }

      if (data.status === "error") {
        if (data.message === "The user already exists") {
          setErrorMessage(TRIAL_DUPLICATE_EMAIL_MESSAGE);
        } else {
          setErrorMessage(data.message || "An error occurred. Please try again.");
        }
        setStatus("form");
        return;
      }

      setErrorMessage("Unexpected response from the server. Please try again.");
      setStatus("form");
    } catch (err) {
      console.error("Trial registration error:", err);
      const guide = trialSignupFailureGuide(err);
      if (err?.response?.data?.code) {
        setErrorMessage(guide.message);
      } else {
        setErrorMessage(mapTrialSignupError(err));
      }
      const holdSubmit = guide.code === "ACCOUNT_SETUP_INCOMPLETE" || (!err?.response && Boolean(err?.request));
      setRecoveryLinks(holdSubmit ? guide.links : []);
      setSubmitLocked(holdSubmit);
      setStatus("form");
    } finally {
      submitting.current = false;
    }
  };

  if (!isOpen) return null;

  return (
    <div
      className="quiet-modal-overlay"
      onClick={handleClose}
      data-testid="lead-modal-overlay"
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="lead-modal-title"
        className="quiet-modal-card"
        onClick={(event) => event.stopPropagation()}
        data-testid="lead-modal"
      >
        <button
          type="button"
          onClick={handleClose}
          className="absolute top-4 right-4 text-[var(--text-muted)] hover:text-[var(--text-main)] transition-colors p-1"
          aria-label="Close dialog"
          disabled={status === "loading"}
        >
          <svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M18 6L6 18M6 6l12 12" />
          </svg>
        </button>

        {status === "success" ? (
          <div className="text-center py-4" data-testid="lead-modal-success">
            <h2 id="lead-modal-title" className="text-xl font-bold text-[var(--text-main)] mb-3">
              You&apos;re all set
            </h2>
            <p className="text-sm text-[var(--text-muted)] mb-6 leading-relaxed">
              {UNIVERSAL_SIGNUP_SUCCESS_MESSAGE}
            </p>
            <Link
              to={UNIVERSAL_SIGNUP_DASHBOARD_ROUTE}
              className="quiet-cta quiet-cta-primary w-full inline-flex items-center justify-center"
              onClick={handleClose}
              data-testid="lead-modal-open-dashboard"
            >
              [ Open Dashboard ↗ ]
            </Link>
          </div>
        ) : (
          <>
            <h2 id="lead-modal-title" className="text-xl font-bold text-[var(--text-main)] pr-8 mb-1">
              {MODAL_TITLE}
            </h2>
            <p className="text-sm text-[var(--text-muted)] mb-5 leading-relaxed">
              {MODAL_SUBTITLE}
            </p>

            <form onSubmit={handleSubmit} className="quiet-modal-form space-y-4" data-testid="lead-modal-form">
              <SignupNicheSelect
                niches={nicheCatalog.niches}
                value={family === "universal" ? nicheId : null}
                onChange={handleNicheChange}
                family={family === "universal" && nicheId ? "universal" : null}
                catalogStatus={nicheCatalog.status}
                errorMessage={nicheCatalog.errorMessage}
                onRetry={nicheCatalog.retry}
                disabled={status === "loading" || submitLocked}
              />

              <div className="quiet-field">
                <label htmlFor="lead-email" className="sr-only">
                  Email
                </label>
                <input
                  ref={emailInputRef}
                  id="lead-email"
                  name="email"
                  type="email"
                  inputMode="email"
                  autoComplete="email"
                  required
                  value={email}
                  onChange={(event) => setEmail(event.target.value)}
                  className="quiet-input"
                  placeholder="you@email.com"
                  disabled={status === "loading" || submitLocked}
                />
              </div>

              {errorMessage && (
                <p className="text-sm text-red-400" role="alert">
                  {errorMessage}
                </p>
              )}
              {recoveryLinks.length > 0 && (
                <p data-testid="lead-modal-recovery">
                  <Link to={recoveryLinks[0]}>Log in</Link>
                  {" "}
                  <Link to={recoveryLinks[1]}>Reset password</Link>
                </p>
              )}

              <button
                type="submit"
                className="quiet-cta quiet-cta-primary w-full"
                disabled={!choiceReady || status === "loading" || submitLocked}
                data-testid="lead-modal-submit"
              >
                {status === "loading" ? "Loading..." : SUBMIT_LABEL}
              </button>
              <div className="cursor-cta-caption-block text-center" data-testid="lead-modal-cta-disclosure">
                <p className="cursor-cta-caption">{CTA_MICRO_DISCLOSURE.line1}</p>
                <LandingPriceLine
                  status={catalogStatus}
                  line={entryLine}
                  className="cursor-cta-caption cursor-cta-caption-secondary"
                />
              </div>
              <p className="quiet-modal-legal">
                By clicking &quot;Open Dashboard&quot;, you agree to our{" "}
                <Link to="/terms" className="quiet-modal-legal-link">
                  Terms of Service
                </Link>
                ,{" "}
                <Link to="/privacy-policy" className="quiet-modal-legal-link">
                  Privacy Policy
                </Link>
                , and{" "}
                <Link to="/dpa" className="quiet-modal-legal-link">
                  Data Processing Agreement (DPA)
                </Link>
                .
              </p>
            </form>
          </>
        )}
      </div>
    </div>
  );
};

export default LeadCaptureModal;
